import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Button,
  FormLabel,
  Textarea,
  useColorModeValue,
  FormControl,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState, useContext } from "react";
import { AppContext } from "contexts/AppContext";
import InputField from "views/admin/events/components/InputField";
import CategoryMultiSelect from "components/CategoryMultiSelect";
import { addMenuCategory } from "Api/apiService";

const normalizeCategory = (value) => String(value || "").trim();
const toKey = (value) => normalizeCategory(value).toLowerCase();

export default function AddHappyHourMenuItemDetailModal({
  isOpen,
  onClose,
  onCreateMenuItem,
}) {
  const [itemName, setItemName] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPrice, setItemPrice] = useState(null);
  const [itemNameError, setItemNameError] = useState("");
  const [itemDescriptionError, setItemDescriptionError] = useState("");
  const [itemPriceError, setItemPriceError] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryInput, setCategoryInput] = useState("");

  const textColor = useColorModeValue("navy.700", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");

  const {
    fetchHappyHourMenuItemData,
    happyHourMenuCategories,
    fetchHappyHourMenuCategories,
  } = useContext(AppContext);

  const categoryOptions = useMemo(
    () => (happyHourMenuCategories || []).map((category) => category.name),
    [happyHourMenuCategories]
  );

  useEffect(() => {
    setItemPrice(null);
    setItemName("");
    setItemDescription("");
    setSelectedCategories([]);
    setCategoryInput("");
    setItemNameError("");
    setItemPriceError("");
    setItemDescriptionError("");
    if (isOpen) {
      fetchHappyHourMenuCategories();
    }
  }, [isOpen]);

  const handleItemNameChange = (e) => {
    setItemName(e.target.value);
  };

  const handleItemDescriptionChange = (e) => {
    setItemDescription(e.target.value);
  };
  const handleItemPriceChange = (e) => {
    setItemPrice(e.target.value);
  };

  const resolveCategories = async () => {
    const trimmedInput = normalizeCategory(categoryInput);
    let nextSelected = selectedCategories;

    if (trimmedInput) {
      const inputKey = toKey(trimmedInput);
      const hasSelected = selectedCategories.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasSelected) {
        nextSelected = [...selectedCategories, trimmedInput];
      }

      const hasOption = categoryOptions.some(
        (category) => toKey(category) === inputKey
      );
      if (!hasOption) {
        try {
          await addMenuCategory({ type: "happyHour", name: trimmedInput });
          fetchHappyHourMenuCategories();
        } catch (error) {
          console.error("Error adding happy hour category:", error);
        }
      }
    }

    return nextSelected;
  };

  const handleSave = async () => {
    try {
      let isValid = true;

      if (!itemName) {
        setItemNameError("Name is required.");
        isValid = false;
      } else {
        setItemNameError("");
      }

      if (!itemDescription) {
        setItemDescriptionError("Description is required.");
        isValid = false;
      } else {
        setItemDescriptionError("");
      }

      if (!itemPrice) {
        setItemPriceError("Price is required.");
        isValid = false;
      } else {
        setItemPriceError("");
      }

      if (!isValid) {
        return;
      }

      const resolvedCategories = await resolveCategories();
      setSelectedCategories(resolvedCategories);
      setCategoryInput("");

      onCreateMenuItem(
        itemName,
        resolvedCategories,
        itemDescription,
        itemPrice
      );
      fetchHappyHourMenuItemData();
      onClose();
      setItemNameError("");
      setItemPriceError("");
      setItemDescriptionError("");
    } catch (error) {
      console.error("Error creating happy hour menu item:", error);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader flex={1} textAlign="center">
          Add Happy Hour Menu Item
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <InputField
            label="Happy Hour Item Name"
            type="text"
            value={itemName}
            onChange={handleItemNameChange}
            errorMessage={itemNameError}
            isRequired={true}
          />

          <FormControl isInvalid={!!itemDescriptionError}>
            <FormLabel
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
            >
              <Flex align="center">
                Item Description<Text color={brandStars}>*</Text>
              </Flex>
            </FormLabel>
            <Textarea
              isInvalid={itemDescriptionError ? true : false}
              isRequired={true}
              fontSize="sm"
              size="lg"
              value={itemDescription}
              onChange={handleItemDescriptionChange}
              mb={itemDescriptionError ? "5px" : "24px"}
            />
            {itemDescriptionError && (
              <Text
                mb="14px"
                color="red.500"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
              >
                {itemDescriptionError}
              </Text>
            )}
          </FormControl>
          <InputField
            label="Happy Hour Item Price"
            type="number"
            value={itemPrice}
            onChange={handleItemPriceChange}
            errorMessage={itemPriceError}
            isRequired={true}
          />
          <CategoryMultiSelect
            label="Item Category"
            options={categoryOptions}
            selected={selectedCategories}
            onSelectedChange={setSelectedCategories}
            inputValue={categoryInput}
            onInputChange={setCategoryInput}
          />
          <Button
            fontSize="sm"
            variant="brand"
            fontWeight="500"
            w="100%"
            h="50"
            mb="24px"
            onClick={handleSave}
          >
            Save
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
